import type { ReactiveChangeOrigin } from '../utils/change-origin/reactive-change-origin';

export type ReactiveFormObject<TModel> = TModel extends object ? TModel : Record<string, never>;

export type ReactiveFormKey<TModel> = Extract<keyof ReactiveFormObject<TModel>, string>;

export type ReactiveFormDynamicKey = string & {
    readonly __reactiveFormDynamicKeyBrand?: unique symbol;
};

/**
 * Runtime value emitted by the reactive form builder.
 *
 * It keeps a typed alias for migrated components while the emitted tuple remains
 * intentionally permissive so legacy handlers that still receive broader tuple
 * shapes can compile during the incremental migration.
 */
export type ReactiveFormValue<TModel> = Partial<ReactiveFormObject<TModel>> & Record<string, unknown>;


/**
 * Name of the control that triggered the latest form emission.
 */
export type ReactiveFormChangedField<TModel> = ReactiveFormKey<TModel> | ReactiveFormDynamicKey;

/**
 * Backward-compatible tuple emitted by `(formChanges)`.
 *
 * Existing templates can continue using:
 * `(formChanges)="formChanges($event[0], $event[1])"`.
 *
 * The first tuple item is deliberately `any` while legacy form components are
 * still being migrated, because some old handlers are typed as `[object, string]`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReactiveFormChange = [any, string];



export interface ReactiveFormValueChange<TModel = unknown> {
    readonly value: ReactiveFormValue<TModel>;
    readonly changedField: ReactiveFormChangedField<TModel>;
    readonly changedFields: readonly string[];
    readonly origin: ReactiveChangeOrigin;
}

export type ReactiveFormRawValueChange<TModel = unknown> = ReactiveFormValueChange<TModel>;

export type ReactiveFormStableValueChange<TModel = unknown> = ReactiveFormValueChange<TModel>;

export type ReactiveFormLifecycleEventType =
    | 'previousDataPatched'
    | 'previousDataChanged'
    | 'dropdownRefresh'
    | 'dropdownDataResolved'
    | 'internalMutation';

export interface ReactiveFormLifecycleEvent<TModel = unknown> {
    readonly type: ReactiveFormLifecycleEventType;
    readonly value: ReactiveFormValue<TModel>;
    readonly changedField: ReactiveFormChangedField<TModel>;
    readonly origin: ReactiveChangeOrigin;
}

export type ReactiveFormFieldValue<
    TModel,
    TFieldName extends keyof ReactiveFormObject<TModel>
> = ReactiveFormObject<TModel>[TFieldName];
export function createReactiveFormChange<T>(
    value: ReactiveFormValue<T>,
    changedField: ReactiveFormChangedField<T>,
): ReactiveFormChange {
    return [value, String(changedField)];
}

export function createReactiveFormValueChange<T>(
    value: ReactiveFormValue<T>,
    changedField: ReactiveFormChangedField<T>,
    changedFields: readonly string[],
    origin: ReactiveChangeOrigin,
): ReactiveFormValueChange<T> {
    return {
        value,
        changedField,
        changedFields,
        origin,
    };
}

export function createReactiveFormLifecycleEvent<T>(
    value: ReactiveFormValue<T>,
    changedField: ReactiveFormChangedField<T>,
    origin: ReactiveChangeOrigin,
): ReactiveFormLifecycleEvent<T> {
    return {
        type: resolveReactiveFormLifecycleEventType(String(changedField)),
        value,
        changedField,
        origin,
    };
}

export function resolveReactiveFormLifecycleEventType(changedField: string): ReactiveFormLifecycleEventType {
    switch (changedField) {
        case '__previous_data_patch':
            return 'previousDataPatched';
        case '__previous_data_change':
            return 'previousDataChanged';
        case '__dropdown_refresh':
            return 'dropdownRefresh';
        default:
            return changedField.startsWith('__') ? 'internalMutation' : 'dropdownDataResolved';
    }
}
